package com.eladfinish.bible.contest.network

import com.eladfinish.bible.contest.currentMilliseconds
import com.eladfinish.bible.contest.network.cache.CacheHelper
import com.eladfinish.bible.contest.network.config.NetworkClientConfiguration
import com.eladfinish.bible.contest.network.logging.NetworkLogger
import com.eladfinish.bible.contest.platform.isWeb
import io.github.aakira.napier.Napier
import io.ktor.client.HttpClient
import io.ktor.client.HttpClientConfig
import io.ktor.client.call.HttpClientCall
import io.ktor.client.plugins.HttpResponseValidator
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.api.createClientPlugin
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logging
import io.ktor.client.statement.HttpReceivePipeline
import io.ktor.client.statement.HttpResponse
import io.ktor.http.Headers
import io.ktor.http.HeadersBuilder
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpProtocolVersion
import io.ktor.http.HttpStatusCode
import io.ktor.serialization.kotlinx.json.json
import io.ktor.util.AttributeKey
import io.ktor.util.date.GMTDate
import io.ktor.utils.io.ByteReadChannel
import io.ktor.utils.io.InternalAPI
import kotlinx.serialization.json.Json
import kotlin.coroutines.CoroutineContext
import kotlin.random.Random

object HttpClientFactory {

    fun createHttpClient(
        clientConfig: NetworkClientConfiguration,
        cacheHelper: CacheHelper?,
    ): HttpClient {
        return HttpClient {
            expectSuccess = true

            installContentNegotiation()
            installLogging(clientConfig.logLevel.ktorLogLevel)
            installTimeouts()
            installRequestTracing()
            installFailureLogging()
            prepareDefaultRequest(clientConfig)

//            install(key = "forceCache", block = forceCache())

//            install(HttpCache) {
//                platformSpecific(
//                    forAndroid = {
//                        publicStorage(storage = cacheHelper.getCacheStorage())
//                    },
//                    forIos = {},
//                )
//            }

        }
    }

    private fun HttpClientConfig<*>.installContentNegotiation() {
        val json = Json {
            ignoreUnknownKeys = true
            explicitNulls = false
            prettyPrint = true
        }

        install(ContentNegotiation) {
            json(json)
        }
    }

    private fun HttpClientConfig<*>.installTimeouts() {
        install(HttpTimeout) {
            requestTimeoutMillis = NetworkTimeouts.REQUEST_TIMEOUT_MS
            connectTimeoutMillis = NetworkTimeouts.CONNECT_TIMEOUT_MS
            socketTimeoutMillis = NetworkTimeouts.SOCKET_TIMEOUT_MS
        }
    }

    private fun HttpClientConfig<*>.installLogging(
        logLevel: LogLevel,
    ) {
        install(Logging) {
            logger = NetworkLogger()
            level = logLevel
        }
    }

    private fun HttpClientConfig<*>.installRequestTracing() {
        install(
            plugin = createClientPlugin(name = "RequestTracingPlugin") {
                onRequest { request, _ ->
                    val existingRequestId = request.headers[REQUEST_ID_HEADER]
                    val requestId = existingRequestId ?: generateRequestId()

                    // On web, adding ANY custom header forces the browser to send a CORS
                    // preflight request, which fails for any endpoint whose CORS policy doesn't
                    // explicitly allow this header (e.g. Google's spreadsheet gviz endpoint) — the
                    // browser then blocks the real request entirely with "Failed to fetch",
                    // before it even reaches the server. This header is only a local
                    // debugging/log-correlation aid (see the attribute below and Napier logs), so
                    // it's safe to just not send it cross-origin on web.
                    if (existingRequestId == null && !isWeb) {
                        request.headers.append(
                            name = REQUEST_ID_HEADER,
                            value = requestId,
                        )
                    }

                    request.attributes.put(
                        key = RequestTracing.requestIdAttributeKey,
                        value = requestId,
                    )

                    Napier.d(
                        message = "HTTP request requestId=$requestId method=${request.method.value} url=${request.url}",
                        tag = LOG_TAG,
                    )
                }

                onResponse { response ->
                    val requestId = response.call.request.attributes
                        .getOrNull(key = RequestTracing.requestIdAttributeKey)
                        ?: response.headers[REQUEST_ID_HEADER]
                        ?: "unknown"

                    Napier.d(
                        message = "HTTP response requestId=$requestId status=${response.status.value} url=${response.call.request.url}",
                        tag = LOG_TAG,
                    )
                }

                onClose {
                    Napier.d(
                        message = "HTTP client plugin closed",
                        tag = LOG_TAG,
                    )
                }
            },
        )
    }

    private fun HttpClientConfig<*>.installFailureLogging() {
        HttpResponseValidator {
            handleResponseExceptionWithRequest { cause, request ->
                val requestId = request.headers[REQUEST_ID_HEADER] ?: "unknown"

                Napier.e(
                    message = "HTTP failure requestId=$requestId method=${request.method.value} url=${request.url} error=${cause.message}",
                    throwable = cause,
                    tag = LOG_TAG,
                )
            }
        }
    }

    private fun HttpClientConfig<*>.prepareDefaultRequest(
        clientConfig: NetworkClientConfiguration,
    ) {
        defaultRequest {
            url(clientConfig.baseUrl)

            headers {
//                append(HttpHeaders.AcceptCharset, "UTF-16")
//                append(HttpHeaders.ContentType, "application/json")
            }
        }
    }

    private fun forceCache(): HttpClient.() -> Unit = {
        receivePipeline.intercept(HttpReceivePipeline.Before) { response ->
            this.proceedWith(object : HttpResponse() {
                override val call: HttpClientCall = response.call
                override val coroutineContext: CoroutineContext = response.coroutineContext
                override val headers: Headers = HeadersBuilder()
                    .apply {
                        appendAll(response.headers)
                        remove(name = HttpHeaders.CacheControl)
                        // 604800 seconds in week
                        // 31536000 seconds in year
                        append(name = HttpHeaders.CacheControl, value = "public, max-age=604800")
                    }
                    .build()
                    .also {
                        Napier.d(
                            message = "forceCache Headers ${it.entries().joinToString(prefix = "\n", separator = "\n")}",
                        )
                    }

                @InternalAPI
                override val rawContent: ByteReadChannel
                    get() = TODO("Not yet implemented")

                override val requestTime: GMTDate = response.requestTime
                override val responseTime: GMTDate = response.responseTime
                override val status: HttpStatusCode = response.status
                override val version: HttpProtocolVersion = response.version
            })
        }
    }

    private fun generateRequestId(): String =
        "${currentMilliseconds}-${Random.nextInt(from = 100000, until = 999999)}"

    private object NetworkTimeouts {
        const val REQUEST_TIMEOUT_MS = 10_000L
        const val CONNECT_TIMEOUT_MS = 10_000L
        const val SOCKET_TIMEOUT_MS = 10_000L
    }

    private object RequestTracing {
        val requestIdAttributeKey: AttributeKey<String> = AttributeKey(name = "request-id")
    }

    private const val REQUEST_ID_HEADER = "X-Request-ID"
    private const val LOG_TAG = "HttpClientFactory"
}
